import { type FrontAuctionBidDTO, type FrontAuctionDTO, useIntl } from '@capsicum/common';
import { useCallback, useMemo } from 'react';

import { type FanShopProductVariantExtended } from '../data-access/queries';
import { AuctionStatusRP } from '../models/auctions-rp';

type UseRPAuctionsSharedProps = {
	isAuctionPage: boolean;
	isOwnBid: boolean;
	variant: FanShopProductVariantExtended & { auctionBids: Array<FrontAuctionBidDTO> };
};

export const useRPAuctionsCommon = ({ isAuctionPage = false, isOwnBid, variant }: UseRPAuctionsSharedProps) => {
	const { $t } = useIntl();

	const texts = useMemo(
		() => ({
			currentBid: $t('rewards.rpAuctions.currentBid'),
			startingBid: $t('rewards.rpAuctions.startingBid'),
			winningBid: $t('rewards.rpAuctions.winningBid'),
			yourBid: $t('rewards.rpAuctions.yourBid'),
			yourWinningBid: $t('rewards.rpAuctions.yourWinningBid')
		}),
		[$t, variant]
	);

	const getAuctionStatusRP = useCallback(
		(variant: FrontAuctionDTO & { auctionBids: Array<FrontAuctionBidDTO> }) => {
			const emptyBids = !isAuctionPage ? !variant?.latestAuctionBid : !variant?.auctionBids?.length;

			const isFinished = variant?.auctionStatus === AuctionStatusRP.FINISHED.toString();

			if (isFinished) {
				if (isOwnBid) {
					return AuctionStatusRP.WON;
				}

				return AuctionStatusRP.WINNING;
			}

			if (isOwnBid) {
				return AuctionStatusRP.OWN;
			}

			if (emptyBids) {
				return AuctionStatusRP.STARTING;
			}

			return AuctionStatusRP.ACTIVE;
		},
		[isOwnBid, variant]
	);

	const bidTitle = useCallback(() => {
		switch (getAuctionStatusRP(variant)) {
			case AuctionStatusRP.WINNING:
				return texts.winningBid;
			case AuctionStatusRP.WON:
				return isAuctionPage ? texts.winningBid : texts.yourWinningBid;
			case AuctionStatusRP.OWN:
				return isAuctionPage ? texts.currentBid : texts.yourBid;
			case AuctionStatusRP.STARTING:
				return texts.startingBid;
			default:
				return texts.currentBid;
		}
	}, [texts, getAuctionStatusRP]);

	return {
		bidTitle
	};
};
